import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { isNullish } from '@/libs/utils'
import type { InfoModalComponent } from '@/components/modal/content/info/InfoModal.vue'
import type { LModalProps, ModalConfig, ModalData } from '@/ts/types/modal.types.ts'

/**
 * The ui store should handle everything which can represents a global ui state, for example
 * loading state, showModal, etc.
 */
export const useUiStore = defineStore('ui', () => {
  const _isLoading = ref(false)
  const _error = ref<Error | string | null>(null)
  const _modal = ref<ModalConfig | null>(null)

  const isLoading = computed(() => {
    return _isLoading.value
  })

  function setLoading(value: boolean) {
    _isLoading.value = value
  }

  const hasError = computed(() => {
    return !!_error.value
  })

  const error = computed(() => {
    if (_error.value instanceof Error) {
      return _error.value?.message
    } else {
      return _error.value
    }
  })

  function setError<E extends Error>(value: E | string) {
    if (isNullish(_error.value)) {
      _error.value = value
    }
  }

  function clearError() {
    _error.value = null
  }

  const showModal = computed(() => {
    return !!_modal.value
  })

  const modal = computed(() => {
    return _modal.value
  })

  /**
   * Set the name of modal to show or hide.
   * If the modal is set to null, the modal will be hidden and the modalData will be cleared.
   * @param component name of the content modal component, for example PurchaseDateModal
   * @param options optional configuration for something like header, footer, etc.
   * @param data optional data to pass to the modal, which can be used by component with useAttrs() composable
   */
  function setModal(
    component: InfoModalComponent | null,
    options?: Partial<LModalProps>,
    data?: ModalData
  ) {
    _modal.value = {
      key: component,
      props: options,
      data
    }

    if (component === null) {
      hideModal()
    }
  }

  function hideModal() {
    _modal.value = null
  }

  return {
    setLoading,
    isLoading,
    hasError,
    error,
    setError,
    clearError,
    showModal,
    setModal,
    hideModal,
    modal
  }
})
